import React, { useState, useEffect } from "react";
import DataTable from "react-data-table-component";
import Sidebar from "../Components/Sidebar";
import Navbar from "../Components/Navbar";
import Footer from "../Components/Footer";
import { Toaster, toast } from "react-hot-toast";
import OverlayLoader from "../Components/OverlayLoader";
import CollapsibleSidebar from "../Components/CollapsibleSidebar";
import { useNavigate, useLocation } from "react-router-dom";
import Select from "react-select";
import {
  fetchMeadiaSourceData,
  fetchAllSubMediaSource,
  fetchUserData,
  StageAnalysisReport,
  FilterStageAsync,
} from "../Services/APIService";

interface StatusCounts {
  [key: string]: number;
}

interface UserSummary {
  userId: number;
  userName: string;
  mobile: string;
  email: string;
  loginUserName: string | null;
  tenantId: number;
  statusCounts: StatusCounts;
  total: number;
}

interface FilterParams {
  tenantId: number;
  fromDate?: string;
  toDate?: string;
  userId?: number;
  sourceId?: number;
  subSourceId?: number;
  leadTypeId?: number;
}

interface MediaSource {
  sourceId: number;
  sourceName: string;
  isActive: boolean;
}

interface SubMediaSource {
  mediaSourceID: number;
  name: string;
  id: number;
  isActive: boolean;
}

interface User {
  userId: number;
  userName: string;
  userTypeId: number;
  isActive: boolean;
  reportTo:number;
}

interface OptionType {
  value: number;
  label: string;
}

type StatusDefinition = {
  key: string; // e.g. "New Lead(1)"
  label: string; // e.g. "New Lead"
  statusId: number; // e.g. 1
};

const ReportView: React.FC = () => {
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [userSummaries, setUserSummaries] = useState<UserSummary[]>([]);
  const [searchText, setSearchText] = useState("");
  const [isFilterExpanded, setIsFilterExpanded] = useState(false);

  // Dropdown data
  const [mediaSources, setMediaSources] = useState<MediaSource[]>([]);
  const [subMediaSources, setSubMediaSources] = useState<SubMediaSource[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [filteredSubSources, setFilteredSubSources] = useState<
    SubMediaSource[]
  >([]);

  // Filter state
  const [filterParams, setFilterParams] = useState<FilterParams>({
    tenantId: parseInt(localStorage.getItem("tenantId") || "1000"),
    fromDate: "",
    toDate: "",
    userId: undefined,
    sourceId: undefined,
    subSourceId: undefined,
    leadTypeId: undefined,
  });
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();

  const [selectedPeriod, setSelectedPeriod] = useState<string>("");

  // dynamic status definitions (built from API keys)
  const [statusDefinitions, setStatusDefinitions] = useState<
    StatusDefinition[]
  >([]);

  const toggleSidebar = () => {
    setIsSidebarCollapsed(!isSidebarCollapsed);
  };

  useEffect(() => {
    const getMenus = async () => {
      setIsLoading(true);
      try {
        const menuID = location.state?.menuID;
        const subMenuId = location.state?.subMenuId;
        const userDataUpdated = location.state?.userDataUpdated;

        const previousMenuID = localStorage.getItem("activeMenuID");
        console.log("MenuID from location state:", menuID);
        if (menuID !== previousMenuID || menuID === null) {
          localStorage.setItem("activeMenuID", menuID);
        } else {
          localStorage.removeItem("activeMenuID");
        }
      } catch (error) {
        console.error("Error fetching menus:", error);
      } finally {
        setIsLoading(false);
      }
    };

    getMenus();
  }, [location.state]);

  // Fetch dropdown data
  const fetchDropdownData = async () => {
    try {
      // Fetch media sources
      const sourceResponse = await fetchMeadiaSourceData();
      setMediaSources(sourceResponse.filter((s: MediaSource) => s.isActive));

      // Fetch sub media sources
      const subSourceResponse = await fetchAllSubMediaSource();
      setSubMediaSources(
        subSourceResponse.filter((s: SubMediaSource) => s.isActive)
      );

      // Fetch users
      const userResponse = await fetchUserData();
      setUsers(
        userResponse.filter(
          (u: User) => (u.userTypeId === 2 || u.userTypeId === 4) && u.isActive
        )
      );
    } catch (error) {
      console.error("Error fetching dropdown data:", error);
      toast.error("Failed to load filter options");
    }
  };

  // helper to build status definition from summaries
  const buildStatusDefinitions = (summaries: UserSummary[]) => {
    if (summaries.length === 0) {
      setStatusDefinitions([]);
      return;
    }

    const first = summaries[0];
    if (!first.statusCounts) {
      setStatusDefinitions([]);
      return;
    }

    const defs: StatusDefinition[] = Object.keys(first.statusCounts).map(
      (key) => {
        // example key: "New Lead(1)" / "Prospect(2)"
        const match = key.match(/^(.*)\((\d+)\)$/);
        const label = match ? match[1].trim() : key;
        const statusId = match ? parseInt(match[2], 10) : 0;

        return { key, label, statusId };
      }
    );

    setStatusDefinitions(defs);
  };

  // Fetch User Details by Tenant ID
  const fetchUserDetails = async () => {
    try {
      setLoading(true);

      const response: any = await StageAnalysisReport();

      // Handle both shapes:
      // 1) { userLeadSummaries: [...] }
      // 2) [ ... ]
      const summaries: UserSummary[] = Array.isArray(response)
        ? response
        : response.userLeadSummaries || [];

      setUserSummaries(summaries);
      buildStatusDefinitions(summaries);
    } catch (error) {
      console.error("Error fetching user details:", error);
      toast.error("Failed to load user details");
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (d: Date): string => {
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  // NEW: Period filter logic
  const handlePeriodChange = (period: string) => {
    setSelectedPeriod(period);

    const today = new Date();
    const year = today.getFullYear();
    const month = today.getMonth(); // 0-based: Jan = 0

    let fromDate: Date;
    let toDate: Date;

    switch (period) {
      case "today": {
        fromDate = new Date(year, month, today.getDate());
        toDate = new Date(year, month, today.getDate());
        break;
      }
      case "yesterday": {
        const y = new Date(year, month, today.getDate() - 1);
        fromDate = y;
        toDate = y;
        break;
      }
      case "thisMonth": {
        // 1st of current month to today
        fromDate = new Date(year, month, 1);
        toDate = today;
        break;
      }
      case "lastMonth": {
        // 🔹 1st of last month
        fromDate = new Date(year, month - 1, 1);
        // 🔹 last day of last month (day 0 of current month)
        toDate = new Date(year, month, 0);
        break;
      }
      case "last3Months": {
        // 🔹 1st of month 3 months ago
        fromDate = new Date(year, month - 3, 1);
        // 🔹 last day of last month
        toDate = new Date(year, month, 0);
        break;
      }
      case "last6Months": {
        // 🔹 1st of month 6 months ago
        fromDate = new Date(year, month - 6, 1);
        // 🔹 last day of last month
        toDate = new Date(year, month, 0);
        break;
      }
      default: {
        // Clear dates if "Select Period" or unknown
        setFilterParams((prev) => ({
          ...prev,
          fromDate: "",
          toDate: "",
        }));
        return;
      }
    }

    // fromDate & toDate always assigned in all non-default cases
    setFilterParams((prev) => ({
      ...prev,
      fromDate: formatDate(fromDate),
      toDate: formatDate(toDate),
    }));
  };

  const handleLeadTypeChange = (leadTypeId: number) => {
    setFilterParams({
      ...filterParams,
      leadTypeId: leadTypeId || undefined,
    });
  };

  // Apply Filter using the new API service
  const applyFilter = async () => {
    try {
      setLoading(true);
      const tenantId = localStorage.getItem("tenantId") || "1000";

      const apiFilterParams: any = {
        tenantId: tenantId,
      };

      if (filterParams.fromDate) {
        apiFilterParams.fromDate = filterParams.fromDate;
      }
      if (filterParams.toDate) {
        apiFilterParams.toDate = filterParams.toDate;
      }
      if (filterParams.userId) {
        apiFilterParams.userId = filterParams.userId;
      }
      if (filterParams.sourceId) {
        apiFilterParams.sourceId = filterParams.sourceId;
      }
      if (filterParams.subSourceId) {
        apiFilterParams.subSourceId = filterParams.subSourceId;
      }
      if (filterParams.leadTypeId) {
        apiFilterParams.inquireId = filterParams.leadTypeId;
      }

      const data: any = await FilterStageAsync(apiFilterParams);
      console.log("Filtered data:", data);

      const summaries: UserSummary[] = Array.isArray(data)
        ? data
        : data.userLeadSummaries || [];

      setUserSummaries(summaries);
      buildStatusDefinitions(summaries);

      toast.success("Filter applied successfully");
    } catch (error) {
      console.error("Error applying filter:", error);
      toast.error("Failed to apply filter");
    } finally {
      setLoading(false);
    }
  };

  // Clear Filter
  const clearFilter = () => {
    setFilterParams({
      tenantId: parseInt(localStorage.getItem("tenantId") || "1000"),
      fromDate: "",
      toDate: "",
      userId: undefined,
      sourceId: undefined,
      subSourceId: undefined,
      leadTypeId: undefined,
    });
    setSelectedPeriod("");
    setFilteredSubSources([]);
    fetchUserDetails();
  };

  // UPDATED: status click now passes date & other filters
  const handleStatusClick = (
    userId: number,
    statusId: number,
    userName: string,
    statusName: string
  ) => {
    const tenantId = localStorage.getItem("tenantId") || "1000";

    const params = new URLSearchParams({
      userId: String(userId),
      tenantId: String(tenantId),
      statusId: String(statusId),
      userName: userName,
      statusName: statusName,
    });

    if (filterParams.fromDate) params.append("fromDate", filterParams.fromDate);
    if (filterParams.toDate) params.append("toDate", filterParams.toDate);
    if (filterParams.leadTypeId)
      params.append("leadTypeId", String(filterParams.leadTypeId));
    if (filterParams.sourceId)
      params.append("sourceId", String(filterParams.sourceId));
    if (filterParams.subSourceId)
      params.append("subSourceId", String(filterParams.subSourceId));

    const url = `/#lead-details?${params.toString()}`;
    window.open(url, "_blank");
  };

  // UPDATED: filtered users – search only by NAME
  const filteredUsers = userSummaries.filter((user) => {
    const text = searchText.toLowerCase().trim();
    if (!text) return true;
    return user.userName.toLowerCase().includes(text);
  });

  // UPDATED: Export to CSV (Excel) – remove Mobile & Email, add Total
  const exportToExcel = () => {
    try {
      const headers = [
        "User Name",
        ...statusDefinitions.map((s) => s.label),
        "Total",
      ];

      const rows = filteredUsers.map((user) => [
        user.userName,
        ...statusDefinitions.map((s) => user.statusCounts[s.key] || 0),
        user.total || 0,
      ]);

      let csvContent = headers.join(",") + "\n";
      rows.forEach((row) => {
        csvContent += row.map((cell) => `"${cell}"`).join(",") + "\n";
      });

      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute(
        "download",
        `user-lead-summary-report-${new Date().toISOString().split("T")[0]}.csv`
      );
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      toast.success("Excel file exported successfully");
    } catch (error) {
      console.error("Error exporting to Excel:", error);
      toast.error("Failed to export Excel file");
    }
  };

  // UPDATED: Export to PDF – remove Mobile/Email, add Total column
  const exportToPDF = () => {
    try {
      const statusHeaderCells = statusDefinitions
        .map((s) => `<th>${s.label}</th>`)
        .join("");

      const bodyRows = filteredUsers
        .map(
          (user) => `
          <tr>
            <td>${user.userName}</td>
            ${statusDefinitions
              .map(
                (s) =>
                  `<td class="status-cell">${user.statusCounts[s.key] || 0
                  }</td>`
              )
              .join("")}
            <td class="status-cell">${user.total || 0}</td>
          </tr>
        `
        )
        .join("");

      const printContent = `
        <!DOCTYPE html>
        <html>
        <head>
          <title>Status Analysis Report</title>
          <style>
            @page {
              size: A4 landscape;
              margin: 1cm;
            }
            body {
              font-family: Arial, sans-serif;
              padding: 20px;
            }
            h1 {
              text-align: center;
              color: #333;
              margin-bottom: 20px;
            }
            table {
              width: 100%;
              border-collapse: collapse;
              margin-top: 20px;
            }
            th, td {
              border: 1px solid #ddd;
              padding: 8px;
              text-align: left;
            }
            th {
              background-color: #a78590;
              color: white;
              font-weight: bold;
            }
            tr:nth-child(even) {
              background-color: #f9f9f9;
            }
            .status-cell {
              text-align: center;
              font-weight: bold;
            }
            .meta-info {
              margin-bottom: 15px;
              font-size: 12px;
              color: #666;
            }
          </style>
        </head>
        <body>
          <h1>Status Analysis Report</h1>
          <div class="meta-info">
            <strong>Generated on:</strong> ${new Date().toLocaleString()}<br/>
            ${filterParams.fromDate
          ? `<strong>From Date:</strong> ${filterParams.fromDate}<br/>`
          : ""
        }
            ${filterParams.toDate
          ? `<strong>To Date:</strong> ${filterParams.toDate}<br/>`
          : ""
        }
            <strong>Total Records:</strong> ${filteredUsers.length}
          </div>
          <table>
            <thead>
              <tr>
                <th>User Name</th>
                ${statusHeaderCells}
                <th>Total</th>
              </tr>
            </thead>
            <tbody>
              ${bodyRows}
            </tbody>
          </table>
        </body>
        </html>
      `;

      const printWindow = window.open("", "_blank");
      if (printWindow) {
        printWindow.document.write(printContent);
        printWindow.document.close();
        printWindow.onload = () => {
          printWindow.print();
          toast.success("PDF print dialog opened");
        };
      } else {
        toast.error("Please allow pop-ups to export PDF");
      }
    } catch (error) {
      console.error("Error exporting to PDF:", error);
      toast.error("Failed to export PDF");
    }
  };

  useEffect(() => {
    fetchUserDetails();
    fetchDropdownData();
  }, []);

  const handleSourceOnChange = (sourceId: number | undefined) => {
    setFilterParams({ ...filterParams, sourceId, subSourceId: undefined });
  };

  useEffect(() => {
    if (filterParams.sourceId && subMediaSources.length > 0) {
      const filtered = subMediaSources.filter(
        (src) => src.mediaSourceID === filterParams.sourceId
      );
      setFilteredSubSources(filtered);
      if (!filtered.some((s) => s.id === filterParams.subSourceId)) {
        setFilterParams((prev) => ({ ...prev, subSourceId: undefined }));
      }
    } else {
      setFilteredSubSources([]);
      setFilterParams((prev) => ({ ...prev, subSourceId: undefined }));
    }
  }, [filterParams.sourceId, subMediaSources]);

  const getSelectedOption = (options: OptionType[], value?: number) => {
    return options.find((option) => option.value === value) || null;
  };

  const sourceOptions: OptionType[] = mediaSources.map((s) => ({
    value: s.sourceId,
    label: s.sourceName,
  }));

  const subSourceOptions: OptionType[] = filteredSubSources.map((s) => ({
    value: s.id,
    label: s.name,
  }));

  const userTypeId = Number(localStorage.getItem("userTypeId"));
  const loggedUserId = Number(localStorage.getItem("UserId"));
  const isLoginAsManager = localStorage.getItem("isLoginAsManager") === "true";

  let filteredUserList = users;

  // Admin → show all users
  if (userTypeId === 1) {
    filteredUserList = users;
  }

  // Manager
  else if (userTypeId === 2) {
    if (isLoginAsManager) {
      // show manager + team
      filteredUserList = users.filter(
        (u) => u.userId === loggedUserId || u.reportTo === loggedUserId
      );
    } else {
      // manager behaving like normal user
      filteredUserList = users.filter((u) => u.userId === loggedUserId);
    }
  }

  // Normal User
  else {
    filteredUserList = users.filter((u) => u.userId === loggedUserId);
  }

  const userOptions: OptionType[] = filteredUserList.map((u) => ({
    value: u.userId,
    label: u.userName,
  }));

  // Dynamic columns from statusDefinitions
  const columns = [
    {
      name: "User Name",
      selector: (row: UserSummary) => row.userName,
      sortable: true,
      width: "220px",
      cell: (row: UserSummary) => (
        <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
          <div
            style={{
              width: "32px",
              height: "32px",
              borderRadius: "50%",
              backgroundColor: "rgb(167, 133, 144)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "white",
              fontSize: "14px",
              fontWeight: "bold",
            }}
          >
            {row.userName.charAt(0).toUpperCase()}
          </div>
          <span style={{ fontWeight: "500", color: "#33475b" }}>
            {row.userName}
          </span>
        </div>
      ),
    },
    // Email column REMOVED (per requirement)
    ...statusDefinitions.map((status) => ({
      name: status.label,
      cell: (row: UserSummary) => (
        <span
          className={`status-count-badge status-${status.statusId}`}
          onClick={() =>
            status.statusId > 0 &&
            handleStatusClick(
              row.userId,
              status.statusId,
              row.userName,
              status.label
            )
          }
          style={{ cursor: "pointer" }}
        >
          {row.statusCounts[status.key] || 0}
        </span>
      ),
      width: "120px",
    })),
    {
      name: "Total",
      cell: (row: UserSummary) => (
        <span
          className="status-count-badge total-badge"
          style={{ cursor: "pointer" }}
        >
          {row.total || 0}
        </span>
      ),
      width: "110px",
    },
  ];

  return (
    <div className="wrapper">
      <Toaster
        position="top-center"
        reverseOrder={false}
        containerStyle={{ zIndex: 10000 }}
      />
      {!isSidebarCollapsed && <CollapsibleSidebar />}
      <div className="main">
        <Navbar onToggleSidebar={toggleSidebar} />
        <div className="main-content-div">
          <div className="main-header-div"></div>
          <main className="content mainContent">
            <div className="container-fluid p-0">
              <div className="row">
                <div className="col-12">
                  <div className="card">
                    <div className="card-header carHeaderWithBtn">
                      <div className="card-title mb-0">
                        Status Analysis Report
                      </div>
                      <div style={{ display: "flex", gap: "10px" }}>
                        <input
                          type="text"
                          placeholder="Search by Name"
                          className="p-2 datatableSearchInput"
                          value={searchText}
                          onChange={(e) => setSearchText(e.target.value)}
                          style={{ width: "250px" }}
                        />
                        <button
                          className="demo-btn btn btn-outline-success btn-sm"
                          onClick={exportToPDF}
                          title="Export to PDF"
                        >
                          <i className="bi bi-file-pdf"></i> Export PDF
                        </button>
                        <button
                          className="demo-btn btn btn-outline-success btn-sm"
                          onClick={exportToExcel}
                          title="Export to Excel"
                        >
                          <i className="bi bi-file-excel"></i> Export Excel
                        </button>
                        <button
                          className="demo-btn btn btn-outline-primary btn-sm"
                          onClick={() => setIsFilterExpanded(!isFilterExpanded)}
                        >
                          <i
                            className={`bi bi-${isFilterExpanded ? "chevron-up" : "funnel"
                              }`}
                          ></i>{" "}
                          {isFilterExpanded ? "Hide Filters" : "Show Filters"}
                        </button>
                      </div>
                    </div>

                    {/* Filter Section */}
                    {isFilterExpanded && (
                      <div
                        className="card-body"
                        style={{
                          borderBottom: "1px solid #dee2e6",
                          backgroundColor: "#f8f9fa",
                        }}
                      >
                        <div className="row g-3">
                          <div className="col-md-3">
                            <label className="form-label">Period</label>
                            <select
                              className="form-select form-control-lg"
                              value={selectedPeriod}
                              onChange={(e) =>
                                handlePeriodChange(e.target.value)
                              }
                            >
                              <option value="">Select Period</option>
                              <option value="today">Today</option>
                              <option value="yesterday">Yesterday</option>
                              <option value="thisMonth">This Month</option>
                              <option value="lastMonth">
                                Last Month (exclude this month)
                              </option>
                              <option value="last3Months">
                                Last 3 Months (exclude this month)
                              </option>
                              <option value="last6Months">
                                Last 6 Months (exclude this month)
                              </option>
                            </select>
                          </div>

                          <div className="col-md-3">
                            <label className="form-label">From Date</label>
                            <input
                              type="date"
                              className="form-control"
                              value={filterParams.fromDate}
                              onChange={(e) =>
                                setFilterParams({
                                  ...filterParams,
                                  fromDate: e.target.value,
                                })
                              }
                            />
                          </div>

                          <div className="col-md-3">
                            <label className="form-label">To Date</label>
                            <input
                              type="date"
                              className="form-control"
                              value={filterParams.toDate}
                              onChange={(e) =>
                                setFilterParams({
                                  ...filterParams,
                                  toDate: e.target.value,
                                })
                              }
                            />
                          </div>

                          <div className="col-md-3">
                            <label className="form-label">Lead Type</label>
                            <select
                              className="form-select form-control-lg"
                              value={filterParams.leadTypeId || ""}
                              onChange={(e) =>
                                handleLeadTypeChange(
                                  parseInt(e.target.value || "0")
                                )
                              }
                            >
                              <option value="">Select Lead Type</option>
                              <option value="1">New Inquiry</option>
                              <option value="6">Reinquiry</option>
                            </select>
                          </div>

                          <div className="col-md-4">
                            <label className="form-label">User</label>
                            <Select
                              options={userOptions}
                              value={getSelectedOption(
                                userOptions,
                                filterParams.userId
                              )}
                              onChange={(option) =>
                                setFilterParams({
                                  ...filterParams,
                                  userId: option?.value,
                                })
                              }
                              className="basic-single-select"
                              classNamePrefix="select"
                              placeholder="Select User"
                              isSearchable
                              isClearable
                            />
                          </div>

                          <div className="col-md-4">
                            <label className="form-label">Source</label>
                            <Select
                              options={sourceOptions}
                              value={getSelectedOption(
                                sourceOptions,
                                filterParams.sourceId
                              )}
                              onChange={(option) =>
                                handleSourceOnChange(option?.value)
                              }
                              className="basic-single-select"
                              classNamePrefix="select"
                              placeholder="Select Source"
                              isSearchable
                              isClearable
                            />
                          </div>

                          <div className="col-md-4">
                            <label className="form-label">Sub Source</label>
                            <Select
                              options={subSourceOptions}
                              value={getSelectedOption(
                                subSourceOptions,
                                filterParams.subSourceId
                              )}
                              onChange={(option) =>
                                setFilterParams({
                                  ...filterParams,
                                  subSourceId: option?.value,
                                })
                              }
                              className="basic-single-select"
                              classNamePrefix="select"
                              placeholder={
                                filterParams.sourceId
                                  ? "Select Sub Source"
                                  : "Select source first"
                              }
                              isSearchable
                              isClearable
                              isDisabled={!filterParams.sourceId}
                            />
                          </div>

                          <div
                            className="col-12"
                            style={{
                              display: "flex",
                              gap: "10px",
                              justifyContent: "flex-end",
                            }}
                          >
                            <button
                              className="btn btn-secondary"
                              onClick={clearFilter}
                            >
                              Clear
                            </button>
                            <button
                              className="btn btn-primary"
                              onClick={applyFilter}
                            >
                              Apply Filter
                            </button>
                          </div>
                        </div>
                      </div>
                    )}

                    <div className="card-body p-0">
                      <DataTable
                        columns={columns}
                        data={filteredUsers}
                        pagination
                        paginationPerPage={10}
                        highlightOnHover
                        striped
                        responsive
                        noDataComponent="No user data found"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </main>
        </div>
        <Footer />
      </div>

      <OverlayLoader
        loading={loading}
        message="Loading Report Data"
        submessage="Please wait..."
      />

      <style>{`
        .rdt_TableCol:first-of-type {
          padding-left: 16px !important;
        }
        
        .status-count-badge {
          padding: 4px 12px;
          border-radius: 12px;
          font-weight: 600;
          font-size: 13px;
          display: inline-block;
          transition: all 0.2s;
        }
        
        .status-count-badge:hover {
          transform: scale(1.1);
          box-shadow: 0 2px 8px rgba(0,0,0,0.15);
        }

        .status-1 { /* New Lead */
          background: #e3f2fd;
          color: #1976d2;
        }
        
        .status-2 { /* Prospect */
          background: #f3e5f5;
          color: #7b1fa2;
        }
        
        .status-3 { /* FollowUp */
          background: #ffebee;
          color: #c62828;
        }
        
        .status-4 { /* Site Visited */
          background: #fff3e0;
          color: #e65100;
        }
        
        .status-5 { /* Bulder Meet */
          background: #e0f2f1;
          color: #00695c;
        }
        
        .status-6 { /* Negotiation */
          background: #fce4ec;
          color: #ad1457;
        }
        
        .status-7 { /* Booking */
          background: #e8f5e9;
          color: #2e7d32;
        }
        
        .status-8 { /* Booking Cancelled */
          background: #ede7f6;
          color: #4527a0;
        }
        
        .status-9 { /* Lost */
          background: #ffebee;
          color: #b71c1c;
        }
        
        .total-badge {
          background: #e8f5e9;
          color: #2e597dff;
        }

        .css-1nmdiq5-menu {
          position: relative;
          z-index: 10000;
        }
      `}</style>
    </div>
  );
};

export default ReportView;
